import { Alert, Button, Modal, Spin } from 'antd';
import { observer } from 'mobx-react-lite';
import { useCallback, useEffect, useRef } from 'react';
import type { ArtifactPreview as Preview } from '../../services/roomInspectorApi';
import type { RoomInspectorStore } from '../../stores/roomInspectorStore';

export function safeExternalURL(input: string): string | undefined {
  try {
    const url = new URL(input);
    return ['http:', 'https:'].includes(url.protocol) ? url.href : undefined;
  } catch {
    return undefined;
  }
}

function SandboxedPreview({ preview, file }: { preview: Preview; file: Blob }) {
  const frame = useRef<HTMLIFrameElement>(null);
  const previewId = useRef(crypto.randomUUID()).current;
  const send = useCallback(
    () =>
      frame.current?.contentWindow?.postMessage(
        {
          type: 'room-inspector.preview',
          blob: file,
          previewId,
          fileName: preview.fileName,
          mimeType: preview.mimeType,
        },
        '*',
      ),
    [preview, file, previewId],
  );
  useEffect(() => {
    const ready = (event: MessageEvent) => {
      if (
        event.source === frame.current?.contentWindow &&
        event.data?.type === 'room-inspector.preview-ready'
      )
        send();
    };
    window.addEventListener('message', ready);
    send();
    return () => window.removeEventListener('message', ready);
  }, [send]);
  return (
    <iframe
      ref={frame}
      onLoad={send}
      title={`文件预览：${preview.fileName}`}
      className="inspector-file-preview"
      src="/room-inspector-preview.html"
      sandbox="allow-scripts allow-downloads"
      referrerPolicy="no-referrer"
    />
  );
}

export const ArtifactPreview = observer(
  ({ store }: { store: RoomInspectorStore }) => {
    const preview = store.preview;
    const url = preview
      ? safeExternalURL(
          preview.artifactType === 'website'
            ? preview.websiteShareUrl
            : preview.url,
        )
      : undefined;
    return (
      <Modal
        title={preview?.fileName || '产物预览'}
        open={store.previewOpen}
        onCancel={store.closePreview}
        footer={null}
        width="min(1100px, 95vw)"
        destroyOnHidden
      >
        {store.previewLoading && <Spin />}
        {store.previewError && (
          <Alert type="error" title={store.previewError} />
        )}
        {preview?.artifactType === 'file' && url && (
          <>
            {store.previewFile && (
              <SandboxedPreview
                key={preview.url}
                preview={preview}
                file={store.previewFile}
              />
            )}
            <Button href={url} target="_blank" rel="noopener noreferrer">
              下载文件
            </Button>
            <p>
              预览链接有效期至{' '}
              {new Date(Number(preview.expiresAtUnixMs)).toLocaleTimeString()}
              ，失效后关闭并重新打开。
            </p>
          </>
        )}
        {preview?.artifactType === 'website' &&
          (url &&
          preview.websiteShareVisibility === 'public' &&
          preview.websiteShareStatus === 'active' ? (
            <Button href={url} target="_blank" rel="noopener noreferrer">
              打开已公开分享的网站
            </Button>
          ) : (
            <Alert
              type="info"
              title="需房间成员身份访问"
              description="该网站没有可用的公开分享链接。"
            />
          ))}
        {preview?.artifactType === 'link' &&
          (url ? (
            <Button href={url} target="_blank" rel="noopener noreferrer">
              打开链接
            </Button>
          ) : (
            <Alert type="info" title="此链接暂不可预览" />
          ))}
      </Modal>
    );
  },
);
